import { lazy, Suspense, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageSquare } from "lucide-react";
import logo from "@/assets/sd-logo.webp";

const ChatBot = lazy(() =>
  import("@/components/sd/ChatBot").then((m) => ({ default: m.ChatBot }))
);

const links = [
  { label: "Startseite", to: "/" },
  { label: "Über uns", to: "/ueber-uns" },
  { label: "Leistungen", to: "/leistungen" },
  { label: "Referenzen", to: "/referenzen" },
  { label: "Kontakt", to: "/kontakt" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 h-[72px] border-b border-border bg-white transition-shadow ${
        scrolled ? "shadow-nav" : ""
      }`}
    >
      <div className="container-narrow flex h-full items-center justify-between">
        <Link to="/" className="flex items-center gap-3" aria-label="S&D Facility Service">
          <img
            src={logo}
            alt="S&D Facility Service Logo"
            width={56}
            height={56}
            className="h-14 w-auto object-contain"
          />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `group relative text-[15px] transition-colors ${
                  isActive
                    ? "text-secondary font-semibold"
                    : "text-muted-foreground hover:text-secondary"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-[2px] bg-secondary transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={() => setChatOpen((v) => !v)}
            aria-label={chatOpen ? "Chatbot schließen" : "Chatbot öffnen"}
            aria-expanded={chatOpen}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-background text-primary transition-all hover:border-secondary hover:text-secondary"
            title="KI-Assistent"
          >
            <MessageSquare className="h-5 w-5" strokeWidth={2} />
          </button>

          <Link
            to="/kontakt"
            className="hidden items-center justify-center rounded-lg bg-accent px-5 py-2.5 text-[15px] font-semibold text-primary transition-colors hover:bg-accent/90 md:inline-flex"
            style={{ letterSpacing: "0.3px" }}
          >
            Jetzt anfragen
          </Link>

          <button
            type="button"
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center justify-center rounded-md p-2 text-primary md:hidden"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {chatOpen && (
        <Suspense fallback={null}>
          <ChatBot open={chatOpen} onClose={() => setChatOpen(false)} />
        </Suspense>
      )}

      {/* Mobile menu */}
      {open && (
        <div className="absolute left-0 right-0 top-[72px] border-b border-border bg-white shadow-nav md:hidden">
          <nav className="container-narrow flex flex-col gap-1 py-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `rounded-md px-3 py-3 text-[15px] transition-colors ${
                    isActive
                      ? "bg-secondary/10 text-secondary font-semibold"
                      : "text-foreground hover:bg-muted"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/kontakt"
              className="mt-2 inline-flex items-center justify-center rounded-lg bg-accent px-5 py-3 text-[15px] font-semibold text-primary"
            >
              Jetzt anfragen
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
